// MfaGate — wraps authenticated routes and enforces two things:
//
//   1. Step-up challenge: any user with at least one verified TOTP factor
//      whose current session AAL is below their next-level AAL gets bounced
//      to /auth/mfa-challenge (e.g. has TOTP but session is still AAL1).
//   2. Enrollment requirement (admins only, #687): an `admin` with ZERO
//      verified TOTP factors gets bounced to /auth/mfa-enroll instead of
//      falling through — MFA is mandatory for admin accounts
//      (docs/sops/enrol-mfa.md), not merely opt-in like it is for every
//      other role.
//
// Supabase exposes `mfa.getAuthenticatorAssuranceLevel()` which returns
// `{ currentLevel, nextLevel }`. If `currentLevel !== nextLevel`, the session
// is below the user's enrolled level and we redirect to /auth/mfa-challenge.
// Critically, `currentLevel === nextLevel` for BOTH "already at AAL2" and
// "never enrolled a factor at all" — the assurance-level check alone can't
// tell those apart, so the enrollment check below does a separate
// `listFactors()` call scoped to admins only.
//
// Reference: https://supabase.com/docs/guides/auth/auth-mfa#aal-mfa-and-row-level-security
//
// We also re-check on every auth state change so a token refresh that drops
// AAL2 (e.g. session restored from cold storage) bounces back to challenge.
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { Loader2, ShieldAlert } from "lucide-react";

import { supabase } from "@/lib/supabase";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Button } from "@/components/ui/button";

type Status =
  | "loading"
  | "ok"
  | "challenge_required"
  | "enrollment_required"
  | "blocked";

export function MfaGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const location = useLocation();
  const { isAdmin, loading: rolesLoading } = useUserRoles();

  useEffect(() => {
    let active = true;

    async function check() {
      if (rolesLoading) {
        // Wait for role data to resolve before deciding the admin-only
        // branches below — evaluating with a stale `isAdmin=false` would let
        // an unenrolled admin through on first render. Leave `status` at its
        // current value (initially "loading") rather than reassigning it
        // synchronously here.
        return;
      }

      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (!active) return;

      if (error) {
        if (isAdmin) {
          // Fail CLOSED for admins — an assurance-level check we can't trust
          // is exactly the situation the enrollment/step-up gate exists to
          // guard, so admin access blocks rather than silently continuing.
          setStatus("blocked");
        } else {
          // Fail open on transient errors for everyone else — better to let
          // the user in than lock them out from a network blip. RLS still
          // gates sensitive data, and non-admins aren't required to enroll.
          setStatus("ok");
        }
        return;
      }

      if (data && data.currentLevel !== data.nextLevel) {
        setStatus("challenge_required");
        return;
      }

      if (isAdmin) {
        const { data: factorsData, error: factorsError } =
          await supabase.auth.mfa.listFactors();
        if (!active) return;
        if (factorsError) {
          setStatus("blocked");
          return;
        }
        const hasVerifiedTotp = (factorsData?.totp ?? []).some(
          (f) => f.status === "verified",
        );
        if (!hasVerifiedTotp) {
          setStatus("enrollment_required");
          return;
        }
      }

      setStatus("ok");
    }

    void check();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void check();
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [isAdmin, rolesLoading]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking session
      </div>
    );
  }

  if (status === "blocked") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center text-sm">
          <ShieldAlert className="h-8 w-8 text-destructive" />
          <p className="font-medium">
            Unable to verify your account's two-factor authentication status.
          </p>
          <p className="text-muted-foreground">
            For security, admin access is blocked until this is resolved.
            Try again, or sign out and back in.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void supabase.auth.signOut()}
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (
    status === "enrollment_required" &&
    location.pathname !== "/auth/mfa-enroll"
  ) {
    return <Navigate to="/auth/mfa-enroll" replace />;
  }

  if (status === "challenge_required" && location.pathname !== "/auth/mfa-challenge") {
    return <Navigate to="/auth/mfa-challenge" replace />;
  }

  return <>{children}</>;
}
